import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { useAuth } from '../../../../providers/AuthContext';
import Spinner from '../../../../components/Spinner';
import { getDataWidget, getWidgetById } from '../../../../services/apiWidgests';
import { useDashboards } from '../../../../providers/DashboardContext';
import DatabaseOffIco from '../../../../components/icons/DatabaseOffIco';
import { useAppContext } from '../../../../providers/AppContext';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const { value, payload: item } = payload[0];
    const formattedValue =
      Number(value) % 1 === 0 ? Number(value) : Number(value).toFixed(2);
    return (
      <div className="bg-black text-white px-2 py-1 rounded opacity-90">
        <div className="text-sm">{item.label}</div>
        <div className="font-semibold">{formattedValue}</div>
      </div>
    );
  }
  return null;
};

const CustomXAxisTick = ({ x, y, payload }) => {
  const parseCustomDate = (dateStr) => {
    // Espera el formato: "dd/mm/yyyy - HH:MM:SS"
    const [datePart, timePart] = dateStr.split('-');

    return [datePart, timePart];
  };

  const [datePart, timePart] = parseCustomDate(payload.value);


  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle" fill="#666">
        {datePart}
      </text>
      <text x={0} y={20} dy={16} textAnchor="middle" fill="#666">
        {timePart}
      </text>
    </g>
  );
};

const CustomLabel = ({ x, y, value, width, height, index }) => {
  const formattedValue =
    Number(value) % 1 === 0 ? Number(value) : Number(value).toFixed(2);

  // Asegurar que el texto no se corte
  const safeY = y < 20 ? y + 20 : y - 10;

  return (
    <text
      x={x}
      y={safeY}
      fill="#374151"
      fontSize={12}
      textAnchor="middle"
    >
      {formattedValue}
    </text>
  );
};

const TelemetryLineChartWidget = ({ id }) => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const {dashboard, selectedDatarange} = useDashboards();
  const [data, setData] = useState([]);
  const [fetchingWidgetData, setFetchingWidgetData] = useState(true);
  const [fetchingWidgetInfo, setFetchingWidgetInfo] = useState(true);
  const [variableLabel, setVariableLabel] = useState("");
  const [timeLabel, setTimeLabel] = useState("");
  const { addToast } = useAppContext();

  const refreshWidgetInfo = async () => {
    try {
      setFetchingWidgetInfo(true);
      const response = await getWidgetById(id, auth?.token);
      setVariableLabel(
        response?.configuration?.variable?.alias ||
        response?.configuration?.variable?.name ||
        ""
      );

      setFetchingWidgetInfo(false);
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetData !== 'undefined') setFetchingWidgetData(false);
        if (typeof setFetchingWidgetInfo !== 'undefined') setFetchingWidgetInfo(false);
        return;
      }
      if (error.response?.status === 404) {
        console.log("Widget no encontrado");
      } else if (error.response?.status === 500) {
        console.log("Error en el servidor, intente nuevamente");
      } else {
        navigate('/logout');
      }
      setFetchingWidgetInfo(false);
    }
  };

  const refreshWidgetData = async () => {
    try {
      setFetchingWidgetData(true);
      const response = await getDataWidget(id, {}, auth?.token) || [];
      setData(response);
      setFetchingWidgetData(false);
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetData !== 'undefined') setFetchingWidgetData(false);
        if (typeof setFetchingWidgetInfo !== 'undefined') setFetchingWidgetInfo(false);
        return;
      }
      if (error.response?.status === 404) {
        console.log("Widget no encontrado");
      } else if (error.response?.status === 500) {
        console.log("Error en el servidor, intente nuevamente");
      } else {
        navigate('/logout');
      }
      setFetchingWidgetData(false);
    }
  };

  useEffect(() => {
    refreshWidgetInfo();
    refreshWidgetData();
  }, [selectedDatarange]);

  return (
    <div className="w-full h-full p-4 bg-white border border-gray-300 rounded">
      {(fetchingWidgetData  || fetchingWidgetInfo)? (
        <div className='w-full h-full flex justify-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-center">
            <div className="w-4 h-4 mr-2 rounded-sm" style={{ backgroundColor: '#3b82f6' }}></div>
            <span className="text-gray-700">
              {variableLabel} vs tiempo
            </span>
          </div>
          {data.length === 0 && (
            <div className='w-full h-full flex flex-col items-center justify-center -mt-8'>
              <span className='text-lg text-gray-700'>No hay datos por el momento</span>
              <DatabaseOffIco width='40px' height='40px'  fill='gray'/>
            </div>

          )}
          {data.length > 0 && (
            <ResponsiveContainer width="100%" height="80%">
              <LineChart
                data={data}
                margin={{ top: 30, right: 20, bottom: 20, left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" stroke="#6b7280"  tick={<CustomXAxisTick />}/>
                <YAxis stroke="#6b7280" />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6 }}
                  isAnimationActive={false}

                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </>
      )}
    </div>
  );
};

export default TelemetryLineChartWidget;
